import { useEffect, useMemo, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import {
  BROADCAST_TEMPLATES,
  AUDIENCES,
  validateBroadcast,
  audienceLabel,
  estimateRecipients,
  type AudienceKey,
  type Channel,
} from '../../lib/broadcast';
import { sendBroadcast, loadBroadcastHistory, type BroadcastHistoryRow } from '../../lib/broadcastService';
import { loadNetworkCustomers } from '../../lib/networkCrmService';
import AnnouncementPanel from './AnnouncementPanel';

/**
 * Messages groupés (parité legacy sa-broadcast) : bandeau + onglets Composer/Historique,
 * templates, audience ciblée, compteur destinataires, canaux Email/Push, aperçu email,
 * envoi via Edge Function send-broadcast. Bandeau réseau accessible en bas.
 */
export default function BroadcastComposer({ tenants = [] }: { tenants?: Tenant[] }) {
  const [tab, setTab] = useState<'compose' | 'history'>('compose');
  const [type, setType] = useState('info');
  const [emoji, setEmoji] = useState('ℹ️');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [audience, setAudience] = useState<AudienceKey>('managers');
  const [channels, setChannels] = useState<Channel[]>(['email']);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [history, setHistory] = useState<BroadcastHistoryRow[]>([]);
  const [customerCount, setCustomerCount] = useState(0);

  useEffect(() => {
    loadBroadcastHistory().then(setHistory);
    loadNetworkCustomers().then((r) => {
      if (r.ok) setCustomerCount(r.customers.length);
    });
  }, []);

  const managersWithEmail = useMemo(() => tenants.filter((t) => t.email && t.email.trim()).length, [tenants]);
  const counts = { tenants: tenants.length, customers: customerCount, loyal: 0, dormant: 0 };
  const recipients = estimateRecipients(audience, counts);
  const withEmail = audience === 'managers' ? managersWithEmail : recipients;

  const errors = validateBroadcast({ type, emoji, title, body, url, audience, channels });
  const toggleChannel = (c: Channel) => setChannels((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  const send = async () => {
    if (errors.length > 0) return;
    if (!confirm(`Envoyer « ${title} » à ${recipients} destinataire(s) (${audienceLabel(audience)}) via ${channels.join(' + ')} ?`)) return;
    setBusy(true);
    setFeedback(null);
    const res = await sendBroadcast({ type, emoji, title, body, url, audience, channels });
    setBusy(false);
    setFeedback(res.message);
    if (res.ok) {
      setTitle('');
      setBody('');
      setUrl('');
      loadBroadcastHistory().then(setHistory);
    }
  };

  const channelCard = (c: Channel, label: string, sub: string) => {
    const on = channels.includes(c);
    return (
      <button
        onClick={() => toggleChannel(c)}
        className={'flex-1 rounded-xl border-2 p-4 text-center ' + (on ? 'border-blue-500 bg-blue-50' : 'border-slate-200')}
      >
        <div className="text-2xl">{c === 'email' ? '✉️' : '🔔'}</div>
        <div className="font-bold">{label}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </button>
    );
  };

  return (
    <div className="max-w-3xl">
      {/* Bandeau */}
      <div className="mb-4 rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)' }}>
        <div className="text-xs font-bold uppercase opacity-80">📣 Messages groupés réseau</div>
        <div className="text-xl font-extrabold">Communiquer avec votre réseau et vos clients</div>
        <div className="text-sm opacity-90">Envoyez une info, une promo, une annonce de fermeture exceptionnelle ou un sondage à des audiences ciblées (gérants, clients, segments).</div>
      </div>

      {/* Onglets */}
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {([['compose', '✍️ Composer'], ['history', `📜 Historique (${history.length})`]] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={'border-b-2 px-4 py-2.5 text-sm font-semibold -mb-px ' + (tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500')}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'history' ? (
        history.length === 0 ? (
          <p className="text-sm opacity-60">Aucune campagne pour l'instant.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <span>{h.emoji} <strong>{h.title}</strong> · {audienceLabel(h.audience as AudienceKey)}</span>
                <span className="text-xs text-slate-500">{(h.channels ?? []).join('+')} · {h.sent_count} envoyé(s) · {new Date(h.created_at).toLocaleDateString('fr-FR')}</span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <>
          {/* Templates */}
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Type de message (pré-template)</label>
          <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {BROADCAST_TEMPLATES.map((t) => (
              <button key={t.key} onClick={() => { setType(t.type); setEmoji(t.emoji); if (!title) setTitle(t.label); }}
                className={'rounded-lg border p-2 text-xs font-semibold ' + (type === t.type ? 'border-blue-500 bg-blue-50' : 'border-slate-200')}>
                <div className="text-base">{t.emoji}</div>{t.label}
              </button>
            ))}
          </div>

          {/* Audience */}
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">À qui ?</label>
          <select value={audience} onChange={(e) => setAudience(e.target.value as AudienceKey)} className="mb-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            {AUDIENCES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
          <p className="mb-4 text-xs text-slate-500">
            <strong>{recipients}</strong> destinataire(s) · {withEmail} avec email · 0 avec push activée
          </p>

          {/* Canaux */}
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Par quel canal ?</label>
          <div className="mb-4 flex gap-2">
            {channelCard('email', 'Email', 'Universel')}
            {channelCard('push', 'Push', 'App installée')}
          </div>

          {/* Emoji + titre */}
          <div className="mb-3 flex gap-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Emoji</label>
              <input value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={2} className="w-14 rounded-lg border border-slate-300 p-2 text-center text-lg" />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Titre *</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Fermeture exceptionnelle samedi" className="w-full rounded-lg border border-slate-300 p-2" />
            </div>
          </div>

          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Message *</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Le contenu détaillé du message…" className="mb-3 w-full rounded-lg border border-slate-300 p-2 text-sm" />

          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Lien (optionnel)</label>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="mb-4 w-full rounded-lg border border-slate-300 p-2 text-sm" />

          {/* Aperçu email */}
          <div className="mb-4 rounded-xl border border-slate-200 p-3">
            <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Aperçu email</div>
            <div className="overflow-hidden rounded-lg">
              <div className="px-4 py-3 font-bold text-white" style={{ background: '#2563EB' }}>{emoji} {title || 'Titre du message'}</div>
              <div className="bg-white px-4 py-3 text-sm text-slate-700">{body || 'Corps du message…'}{url && <div className="mt-2 text-blue-600 underline">{url}</div>}</div>
            </div>
          </div>

          {errors.length > 0 && <p className="mb-2 text-sm text-red-600">{errors.join(' · ')}</p>}
          {feedback && <p className="mb-2 text-sm">{feedback}</p>}
          <button onClick={send} disabled={busy || errors.length > 0} className="w-full rounded-xl py-3 font-bold text-white disabled:opacity-40" style={{ background: '#2563EB' }}>
            {busy ? 'Envoi…' : `📨 Envoyer maintenant à ${recipients} destinataire(s)`}
          </button>

          {/* Bandeau réseau (persistant sur tous les sites) */}
          <details className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
            <summary className="cursor-pointer text-sm font-semibold text-slate-600">🚩 Gérer le bandeau réseau (affiché sur tous les sites)</summary>
            <div className="mt-3">
              <AnnouncementPanel />
            </div>
          </details>
        </>
      )}
    </div>
  );
}
